import { s } from '../lib/sx'

/**
 * An image frame.
 *
 * With a `src` it renders the photograph, cropped to fill whatever aspect ratio
 * the caller asks for. Without one it falls back to the drawing-grid placeholder
 * from the prototype, so a frame with no photograph yet still reads as a
 * deliberate part of the design rather than a hole.
 *
 * Photographs are credited in `public/images/CREDITS.json`.
 */
export type PlateTone = 'light' | 'dark' | 'mid'

const BODY: Record<PlateTone, string> = {
  light:
    'background:linear-gradient(#D5DAE0 1px,transparent 1px),linear-gradient(90deg,#D5DAE0 1px,transparent 1px);background-size:26px 26px;background-color:#E7EAEE;box-shadow:inset 0 0 0 1px #C3C9D1;',
  mid: 'background:linear-gradient(#CBD1D8 1px,transparent 1px),linear-gradient(90deg,#CBD1D8 1px,transparent 1px);background-size:26px 26px;background-color:#DCE0E5;box-shadow:inset 0 0 0 1px #C3C9D1;',
  dark: 'background:linear-gradient(#2A3746 1px,transparent 1px),linear-gradient(90deg,#2A3746 1px,transparent 1px);background-size:26px 26px;background-color:#212C39;box-shadow:inset 0 0 0 1px #3D4A59;',
}

/** Ground colour behind a photograph while it loads. */
const PHOTO_BODY: Record<PlateTone, string> = {
  light: 'background-color:#E7EAEE;box-shadow:inset 0 0 0 1px #C3C9D1;',
  mid: 'background-color:#DCE0E5;box-shadow:inset 0 0 0 1px #C3C9D1;',
  dark: 'background-color:#212C39;box-shadow:inset 0 0 0 1px #3D4A59;',
}

const CAPTION: Record<PlateTone, string> = {
  light: "font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#66707C;background:#F2F4F6;",
  mid: "font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#47515D;background:#F2F4F6;",
  dark: "font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#B8C0CA;background:#1B2430;",
}

const IMG = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;'

export interface PlateProps {
  tone?: PlateTone
  /** Extra declarations for the outer box — aspect ratio, padding, margin. */
  box: string
  /** Photograph to fill the frame. Omit to show the drawing-grid placeholder. */
  src?: string
  caption?: string
  /** Extra declarations for the caption chip. */
  captionStyle?: string
  /** Load eagerly for anything above the fold. */
  eager?: boolean
}

export function Plate({ tone = 'light', box, src, caption, captionStyle = 'padding:6px 10px;', eager }: PlateProps) {
  const base = src ? PHOTO_BODY[tone] : BODY[tone]
  return (
    <div style={s(`position:relative;overflow:hidden;${base}${box}`)}>
      {src && (
        <img
          src={src}
          alt={caption ?? ''}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          style={s(IMG)}
        />
      )}
      {caption && (
        <span style={s(`position:relative;z-index:1;${CAPTION[tone]}${captionStyle}`)}>{caption}</span>
      )}
    </div>
  )
}
